import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit, Trash2, Monitor, Smartphone, Beaker, Layout, ImageIcon } from 'lucide-react';
import ProjectEditPage from '../ProjectEditPage';
import StudioItemForm from '../StudioItemForm';

import aitopiaImg from '../../assets/projects/aitopia.webp';
import defactoImg from '../../assets/projects/defactofit.webp';
import soqratImg from '../../assets/projects/soqrat.png';
import vivooImg from '../../assets/projects/vivoo.webp';
import cleanerImg from '../../assets/projects/cleaner.webp';
import briefioImg from '../../assets/projects/briedio.webp';
import artieImg from '../../assets/projects/artie.webp';
import easyGiveawayImg from '../../assets/projects/easy_giveaway.jpg';
import fittyImg from '../../assets/projects/fitty.webp';
import mrmocoImg from '../../assets/projects/mrmoco.webp';
import taomImg from '../../assets/projects/taom.webp';
import wsiImg from '../../assets/projects/wsi.webp';
import zompahImg from '../../assets/projects/zompah.png';
import perscentImg from '../../assets/projects/perscent.webp';
import skytagsImg from '../../assets/projects/skytags.webp';

const LOCAL_IMAGE_MAP = {
  'aitopia':       { src: aitopiaImg,      name: 'aitopia.webp' },
  'defactofit':    { src: defactoImg,      name: 'defactofit.webp' },
  'soqrateque':    { src: soqratImg,       name: 'soqrat.png' },
  'vivoo':         { src: vivooImg,        name: 'vivoo.webp' },
  'cleaner':       { src: cleanerImg,      name: 'cleaner.webp' },
  'briefio':       { src: briefioImg,      name: 'briefio.webp' },
  'artie':         { src: artieImg,        name: 'artie.webp' },
  'easy-giveaway': { src: easyGiveawayImg, name: 'easy_giveaway.jpg' },
  'fitty':         { src: fittyImg,        name: 'fitty.webp' },
  'mrmoco':        { src: mrmocoImg,       name: 'mrmoco.webp' },
  'taom':          { src: taomImg,         name: 'taom.webp' },
  'wsi':           { src: wsiImg,          name: 'wsi.webp' },
  'zompah':        { src: zompahImg,       name: 'zompah.png' },
  'perscent':      { src: perscentImg,     name: 'perscent.webp' },
  'skytags':       { src: skytagsImg,      name: 'skytags.webp' },
};

const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 640px) {
    align-items: stretch;
    margin-bottom: 20px;
  }
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: 2rem;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;

  @media (max-width: 640px) {
    font-size: 1.55rem;
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 28px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar { display: none; }

  @media (max-width: 640px) {
    margin-bottom: 20px;
  }
`;

const Tab = styled.button`
  background: none;
  border: none;
  color: ${({ theme, $active }) => $active ? theme.colors.text.primary : theme.colors.text.muted};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  padding: 10px 16px;
  margin-bottom: -1px;
  border-bottom: 2px solid ${({ theme, $active }) => $active ? theme.colors.accent : 'transparent'};
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  transition: color 160ms ease;

  &:hover { color: ${({ theme }) => theme.colors.text.primary}; }

  @media (max-width: 640px) {
    font-size: 0.88rem;
    padding: 10px 12px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    width: 100%;

    > * { flex: 1 1 100%; justify-content: center; }
  }
`;

const AddButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 18px;
  background: ${({ theme }) => theme.colors.accent};
  border: none;
  border-radius: 8px;
  color: #fff;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: opacity 160ms ease;

  &:hover { opacity: 0.9; }
`;

const MigrateButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-weight: 500;
  font-size: 0.88rem;
  cursor: pointer;
  transition: border-color 160ms ease, color 160ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const MigrateStatus = styled.p`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.text.muted};
  margin: 8px 0 0;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const ProjectCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: border-color 160ms ease, transform 160ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    transform: translateY(-2px);
  }
`;

const ProjectImage = styled.div`
  width: 100%;
  height: 180px;
  background: ${({ theme }) => theme.colors.bg};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProjectInfo = styled.div`
  padding: 18px;
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 8px;
`;

const ProjectTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 1.05rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 6px;
`;

const ProjectType = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const ProjectDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.88rem;
  line-height: 1.55;
  margin: 10px 0 14px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ProjectActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled(motion.button)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px;
  background: transparent;
  border: 1px solid ${({ theme, $danger }) => $danger ? 'rgba(239, 68, 68, 0.3)' : theme.colors.border};
  border-radius: 8px;
  color: ${({ theme, $danger }) => $danger ? '#ef4444' : theme.colors.text.secondary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 160ms ease, color 160ms ease, border-color 160ms ease;

  &:hover {
    background: ${({ theme, $danger }) => $danger ? 'rgba(239, 68, 68, 0.08)' : `rgba(${theme.colors.textRgb}, 0.04)`};
    color: ${({ theme, $danger }) => $danger ? '#ef4444' : theme.colors.text.primary};
    border-color: ${({ theme, $danger }) => $danger ? 'rgba(239, 68, 68, 0.5)' : theme.colors.borderStrong};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.colors.text.muted};

  h3 {
    font-family: ${({ theme }) => theme.fonts.display};
    font-style: italic;
    font-weight: 500;
    font-size: 1.3rem;
    margin-bottom: 8px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  p { font-size: 0.95rem; }
`;

const PortfolioPage = () => {
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [studioItems, setStudioItems] = useState([]);

  const [editPageOpen, setEditPageOpen] = useState(false);
  const [editPageProject, setEditPageProject] = useState(null);

  const [showStudioForm, setShowStudioForm] = useState(false);
  const [editingStudioItem, setEditingStudioItem] = useState(null);

  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [migrateStatus, setMigrateStatus] = useState('');

  const loadProjects = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'projects'));
      setProjects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  }, []);

  const loadStudioItems = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'studio_items'));
      setStudioItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading studio items:', err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadProjects(), loadStudioItems()]);
      setLoading(false);
    };
    init();
  }, [loadProjects, loadStudioItems]);

  const handleDelete = async (projectId) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await deleteDoc(doc(db, 'projects', projectId));
      setProjects(projects.filter(p => p.id !== projectId));
    } catch (err) {
      console.error(err);
      alert('Failed to delete project');
    }
  };

  const handleDeleteStudioItem = async (itemId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await deleteDoc(doc(db, 'studio_items', itemId));
      setStudioItems(studioItems.filter(i => i.id !== itemId));
    } catch (err) {
      console.error(err);
      alert('Failed to delete product');
    }
  };

  const handleEdit = (project) => {
    setEditPageProject(project);
    setEditPageOpen(true);
  };

  const handleFormClose = () => {
    setEditPageOpen(false);
    setEditPageProject(null);
    loadProjects();
  };

  const handleEditStudioItem = (item) => {
    setEditingStudioItem(item);
    setShowStudioForm(true);
  };

  const handleStudioFormClose = () => {
    setShowStudioForm(false);
    setEditingStudioItem(null);
    loadStudioItems();
  };

  const withTimeout = (promise, ms) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Timeout after ${ms / 1000}s`)), ms)
      ),
    ]);

  const handleMigrateImages = async () => {
    setMigrating(true);
    setMigrateStatus('Starting…');

    const entries = Object.entries(LOCAL_IMAGE_MAP);
    let done = 0;
    const failed = [];

    for (const [projectId, { src, name }] of entries) {
      setMigrateStatus(`Uploading ${projectId} (${done + 1}/${entries.length})…`);
      try {
        const response = await withTimeout(fetch(src), 15000);
        const blob = await response.blob();
        const storageRef = ref(storage, `projects/${projectId}/${name}`);
        await withTimeout(uploadBytes(storageRef, blob), 30000);
        const url = await withTimeout(getDownloadURL(storageRef), 15000);
        await updateDoc(doc(db, 'projects', projectId), { images: [url] });
        done++;
      } catch (err) {
        console.error(`Failed to migrate ${projectId}:`, err.message);
        failed.push(projectId);
      }
    }

    const failMsg = failed.length ? ` (${failed.length} failed: ${failed.join(', ')})` : '';
    setMigrateStatus(`Done — ${done}/${entries.length} uploaded.${failMsg}`);
    setMigrating(false);
    loadProjects();
  };

  if (editPageOpen) {
    return <ProjectEditPage project={editPageProject} onClose={handleFormClose} />;
  }

  return (
    <>
      <Tabs>
        <Tab $active={activeTab === 'projects'} onClick={() => setActiveTab('projects')}>
          <Layout size={20} />
          Client Projects
        </Tab>
        <Tab $active={activeTab === 'studio'} $type="studio" onClick={() => setActiveTab('studio')}>
          <Beaker size={20} />
          Our Products
        </Tab>
      </Tabs>

      <TitleBar>
        <Title>{activeTab === 'projects' ? 'Client Portfolio' : 'Our Products'}</Title>
        <ButtonRow>
          {activeTab === 'projects' && (
            <MigrateButton
              onClick={handleMigrateImages}
              disabled={migrating}
              whileHover={{ scale: migrating ? 1 : 1.05 }}
              whileTap={{ scale: migrating ? 1 : 0.95 }}
            >
              <ImageIcon size={18} />
              {migrating ? 'Uploading…' : 'Upload Images to Firebase'}
            </MigrateButton>
          )}

          {activeTab === 'projects' ? (
            <AddButton
              onClick={() => { setEditPageProject(null); setEditPageOpen(true); }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Plus size={20} />
              Add Client Project
            </AddButton>
          ) : (
            <AddButton
              $studio
              onClick={() => setShowStudioForm(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Plus size={20} />
              Add Product
            </AddButton>
          )}
        </ButtonRow>
      </TitleBar>

      {migrateStatus && <MigrateStatus>{migrateStatus}</MigrateStatus>}

      {loading ? (
        <EmptyState><h3>Loading…</h3></EmptyState>
      ) : activeTab === 'projects' ? (
        projects.length === 0 ? (
          <EmptyState>
            <h3>No Projects Yet</h3>
            <p>Click "Add Client Project" to create your first project</p>
          </EmptyState>
        ) : (
          <ProjectsGrid>
            <AnimatePresence>
              {projects.map(project => (
                <ProjectCard
                  key={project.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <ProjectImage>
                    {project.images && project.images.length > 0 ? (
                      <img src={project.images[0]} alt={project.title} />
                    ) : (
                      <div style={{ color: 'rgba(255,255,255,0.3)' }}>No Image</div>
                    )}
                  </ProjectImage>
                  <ProjectInfo>
                    <ProjectHeader>
                      <div>
                        <ProjectTitle>{project.title}</ProjectTitle>
                        <ProjectType $type={project.type}>
                          {project.type === 'web' ? <Monitor size={14} /> : <Smartphone size={14} />}
                          {project.type === 'web' ? 'Web' : 'Mobile'}
                        </ProjectType>
                      </div>
                    </ProjectHeader>
                    <ProjectDescription>{project.description}</ProjectDescription>
                    <ProjectActions>
                      <ActionButton onClick={() => handleEdit(project)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Edit size={16} />
                        Edit
                      </ActionButton>
                      <ActionButton $danger onClick={() => handleDelete(project.id)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Trash2 size={16} />
                        Delete
                      </ActionButton>
                    </ProjectActions>
                  </ProjectInfo>
                </ProjectCard>
              ))}
            </AnimatePresence>
          </ProjectsGrid>
        )
      ) : (
        studioItems.length === 0 ? (
          <EmptyState>
            <h3>No Products Yet</h3>
            <p>Click "Add Product" to create your first product</p>
          </EmptyState>
        ) : (
          <ProjectsGrid>
            <AnimatePresence>
              {studioItems.map(item => (
                <ProjectCard
                  key={item.id}
                  $studio
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <ProjectImage>
                    {item.images && item.images.length > 0 ? (
                      <img src={item.images[0]} alt={item.title} />
                    ) : (
                      <div style={{ color: 'rgba(255,255,255,0.3)' }}>No Image</div>
                    )}
                  </ProjectImage>
                  <ProjectInfo>
                    <ProjectHeader>
                      <div>
                        <ProjectTitle>{item.title}</ProjectTitle>
                        <ProjectType $studio>
                          <Beaker size={14} />
                          {item.type}
                        </ProjectType>
                      </div>
                    </ProjectHeader>
                    <ProjectDescription>{item.description}</ProjectDescription>
                    <ProjectActions>
                      <ActionButton onClick={() => handleEditStudioItem(item)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Edit size={16} />
                        Edit
                      </ActionButton>
                      <ActionButton $danger onClick={() => handleDeleteStudioItem(item.id)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Trash2 size={16} />
                        Delete
                      </ActionButton>
                    </ProjectActions>
                  </ProjectInfo>
                </ProjectCard>
              ))}
            </AnimatePresence>
          </ProjectsGrid>
        )
      )}

      <AnimatePresence>
        {showStudioForm && (
          <StudioItemForm item={editingStudioItem} onClose={handleStudioFormClose} />
        )}
      </AnimatePresence>
    </>
  );
};

export default PortfolioPage;
